import { useMemo, type ReactNode } from 'react';
import { ArrowLeft, BadgeCheck, Check, CheckCircle2, Edit, XCircle } from 'lucide-react';

import { getLocale } from '../../../i18n';
import { compact } from '../../../i18n/format';

import { EmbeddedSwitcher } from '../../../chrome/entity/EntitySwitcher';

import { HintedIcon } from '../../../chrome/hint';
import { Absent } from '../../../chrome/measure';
import { useBreadcrumbLeaf } from '../../../chrome/shell';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Chip } from '../../../ui/chip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { Skeleton } from '../../../ui/skeleton';
import { AccountAvatar } from '../../data/media/AccountAvatar';
import { useTagVocabulary } from '../../tenant/tags/api/queries';
import type { TagCategory } from '../../tenant/tags/api/schemas';
import { DEFAULT_TAG_ICON, TagGlyph, tagIconName } from '../../tenant/tags/icons';
import { useMonitoringProfiles } from '../../control/pages/Catalogue';
import {
  useAccountTracking,
  useSetAlignment,
  useTrackedAccounts,
  type ReadingWindow,
} from './api/queries';
import type { TrackedAccountRow } from './api/schemas';
import { AlignmentChip } from './AccountCard';
import { CollectionChips } from './CollectionChips';
import { AnalysisScopeSelect } from './account/AnalysisScopeSelect';
import { CandidateSelect } from './account/CandidateSelect';
import { CommentSentiment } from './account/CommentSentiment';
import { KpiRow } from './account/KpiRow';
import { Publications } from './account/Publications';
import { ReachOverTime } from './account/ReachOverTime';
import { AccountSectionsSkeleton } from './account/SectionsSkeleton';
import { Topics } from './account/Topics';
import { am } from './messages';
import { platformLabel, PlatformIcon } from './NoSelection';
import { RemoveAccount } from './RemoveAccount';
import { RenameAccount } from './RenameAccount';

export interface AccountProps {
  campaignId: string;
  accountId: string;
  isAnalyst?: boolean;
  onBack?: () => void;
  onEdit?: () => void;
  /** The organisation whose profiles the account's collection is read from. */
  tenantId?: string | null;
}

const READING_WINDOW_DAYS = 7;

/** The window every 75:2 section reads. Computed once per mount, not on every render, so a
 *  section re-rendering mid-fetch never sends a second request with a slightly later `to`. */

/** What the latest profile collection read: display name, verified, bio and the three figures;
 *  nothing at all before the first one. */
function ProfileLine(props: { account: TrackedAccountRow }): ReactNode {
  const { account } = props;
  const locale = getLocale();
  const shown: [number, string][] = [];
  if (account.followers != null) shown.push([account.followers, am('profile.followers')]);
  if (account.following != null) shown.push([account.following, am('profile.following')]);
  if (account.posts_count != null) shown.push([account.posts_count, am('profile.posts')]);
  if (account.bio == null && account.display_name == null && shown.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5 border-t pt-3">
      {account.display_name != null && (
        <p className="flex items-center gap-1 text-sm font-medium">
          {account.display_name}
          {account.verified === true && (
            <HintedIcon label={am('card.verified')} className="text-primary">
              <BadgeCheck aria-hidden className="size-4" />
            </HintedIcon>
          )}
        </p>
      )}
      {account.bio != null && account.bio !== '' && (
        <p className="whitespace-pre-line text-sm text-muted-foreground">{account.bio}</p>
      )}
      {shown.length > 0 && (
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {shown.map(([value, label]) => (
            <span key={label} className="flex items-baseline gap-1">
              <span className="font-semibold text-foreground tabular-nums">
                {compact(value, locale)}
              </span>
              <span>{label}</span>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

export function useTrailingWindow(days: number): ReadingWindow {
  return useMemo(() => {
    const now = new Date();
    const from = new Date(now.getTime() - days * 86_400_000);
    return { from: from.toISOString(), to: now.toISOString() };
  }, [days]);
}

/** The account's alignment chip; a writer opens it to move the account to another value of the
 *  alignment category, the four fixed ones and any the organisation added. */
function AlignmentField(props: {
  campaignId: string;
  account: TrackedAccountRow;
  categories: TagCategory[] | undefined;
  canWrite: boolean;
}): ReactNode {
  const setAlignment = useSetAlignment(props.campaignId, props.account.id);
  const alignment = props.categories?.find((category) => category.locked);
  const chip = <AlignmentChip account={props.account} />;
  if (!props.canWrite || alignment === undefined) return chip;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={am('alignment.change')}
        disabled={setAlignment.isPending}
        className="focus-visible:ring-ring/50 rounded-sm outline-none focus-visible:ring-3"
      >
        {chip}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {alignment.values
          .filter((value) => value.active)
          .map((value) => (
            <DropdownMenuItem
              key={value.id}
              onSelect={() => setAlignment.mutate({ value_id: value.id })}
            >
              <TagGlyph name={tagIconName(value, alignment)} />
              {value.name}
              {value.id === props.account.alignment_value_id && (
                <Check aria-hidden className="ms-auto" />
              )}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AccountHeaderSections(props: {
  campaignId: string;
  accountId: string;
  window: ReadingWindow;
}): ReactNode {
  return (
    <>
      <KpiRow
        campaignId={props.campaignId}
        trackedAccountId={props.accountId}
        window={props.window}
      />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="flex flex-col gap-4">
          <ReachOverTime
            campaignId={props.campaignId}
            trackedAccountId={props.accountId}
            window={props.window}
          />
          <Publications
            campaignId={props.campaignId}
            trackedAccountId={props.accountId}
            window={props.window}
          />
        </div>
        <div className="flex flex-col gap-4">
          <CommentSentiment
            campaignId={props.campaignId}
            trackedAccountId={props.accountId}
            window={props.window}
          />
          <Topics
            campaignId={props.campaignId}
            trackedAccountId={props.accountId}
            window={props.window}
          />
        </div>
      </div>
    </>
  );
}

export function Account({
  campaignId,
  accountId,
  tenantId = null,
  isAnalyst = false,
  onBack,
  onEdit,
}: AccountProps): ReactNode {
  const window_ = useTrailingWindow(READING_WINDOW_DAYS);
  const accountsQuery = useTrackedAccounts(campaignId);
  const trackingQuery = useAccountTracking(campaignId, accountId);
  const profilesQuery = useMonitoringProfiles(tenantId ?? '');
  const vocabularyQuery = useTagVocabulary(campaignId);
  const listed = accountsQuery.data?.find((a) => a.id === accountId);
  useBreadcrumbLeaf(
    listed === undefined
      ? undefined
      : (listed.label ?? (listed.handle ? `@${listed.handle}` : am('unknownHandle'))),
  );

  if (trackingQuery.isLoading || accountsQuery.isLoading) {
    return (
      <div className="space-y-6 w-full">
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 rounded-md" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Skeleton className="size-4 rounded-full" />
          <span>{am('detail.loading')}</span>
        </div>
        <AccountSectionsSkeleton />
      </div>
    );
  }

  if (trackingQuery.isError || accountsQuery.isError) {
    return (
      <Card className="w-full border-destructive/50 bg-destructive/5">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-destructive">
            {am('error.title')}
          </CardTitle>
          <CardDescription className="text-destructive/80">{am('detail.error')}</CardDescription>
        </CardHeader>
        {onBack && (
          <CardContent>
            <Button variant="outline" size="sm" onClick={onBack}>
              <ArrowLeft className="mr-1.5 size-4" aria-hidden="true" />
              {am('detail.back')}
            </Button>
          </CardContent>
        )}
      </Card>
    );
  }

  const account = accountsQuery.data?.find((a) => a.id === accountId);
  const tracking = trackingQuery.data;

  const handleDisplay = account?.handle ? `@${account.handle}` : am('unknownHandle');
  const displayedName = account?.label ?? handleDisplay;
  const platform = account?.platform ?? 'instagram';
  const profile = profilesQuery.data?.items.find((item) => item.id === tracking?.profile_id);
  const profileName = account?.profile_name ?? profile?.name;

  const postMedia = profile?.media.find((m) => m.media_kind === 'POST');
  const commentsEnabled = postMedia?.comments_enabled ?? false;
  const commentCap = postMedia?.comment_cap_per_post;
  const likersEnabled = postMedia?.likers_enabled ?? false;
  const audioFanout = profile?.audio.enabled ?? false;

  const otherTags = (account?.account_tags ?? []).filter(
    (tag) => tag.value_id !== account?.alignment_value_id,
  );

  return (
    <div className="space-y-6 w-full">
      <Card className="border-border bg-card">
        <CardContent className="flex flex-col gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <h1 className="min-w-0 text-xl font-bold tracking-tight sm:text-2xl">
              <EmbeddedSwitcher>
                <AccountAvatar
                  size="lg"
                  className="border-2 border-border shadow-xs"
                  avatarUrl={account?.avatar_url ?? null}
                  name={account?.label ?? account?.handle ?? null}
                />
                <span className="min-w-0 truncate">{displayedName}</span>
              </EmbeddedSwitcher>
            </h1>
            {!isAnalyst && account && (
              <RenameAccount
                campaignId={campaignId}
                trackedAccountId={accountId}
                label={account.label}
                handle={account.handle}
              />
            )}
            <div className="ms-auto flex shrink-0 items-center gap-1">
              {!isAnalyst && (
                <Button onClick={onEdit} variant="outline" size="sm">
                  <Edit aria-hidden="true" />
                  <span className="sr-only sm:not-sr-only">{am('detail.edit')}</span>
                </Button>
              )}
              {!isAnalyst && account && (
                <RemoveAccount
                  campaignId={campaignId}
                  trackedAccountId={accountId}
                  name={displayedName}
                  onRemoved={onBack}
                />
              )}
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <HintedIcon label={platformLabel(platform)}>
              <PlatformIcon platform={platform} className="size-4 shrink-0" />
            </HintedIcon>
            <span className="font-medium text-foreground">{platformLabel(platform)}</span>
            {/* eslint-disable-next-line no-restricted-syntax -- middot separator, identical in both locales */}
            <span>·</span>
            <span>{profileName ? profileName : am('defaultProfile')}</span>
            {account && (
              <AlignmentField
                campaignId={campaignId}
                account={account}
                categories={vocabularyQuery.data}
                canWrite={!isAnalyst}
              />
            )}
            {otherTags.map((tag) => (
              <Chip
                key={tag.value_id}
                size="sm"
                tone="outline"
                icon={<TagGlyph name={tag.icon ?? DEFAULT_TAG_ICON} />}
              >
                <span className="text-muted-foreground">
                  {am('tag.category', { category: tag.category })}
                </span>
                {tag.value}
              </Chip>
            ))}
          </div>
          {account && <ProfileLine account={account} />}
        </CardContent>
      </Card>

      <AccountHeaderSections campaignId={campaignId} accountId={accountId} window={window_} />

      <Card className="border-border">
        <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-start">
          <AnalysisScopeSelect campaignId={campaignId} trackedAccountId={accountId} />
          {account?.alignment === 'ADVERSARY' && tenantId !== null && (
            <CandidateSelect
              campaignId={campaignId}
              tenantId={tenantId}
              trackedAccountId={accountId}
            />
          )}
        </CardContent>
      </Card>

      {/* Tracking Summary Card */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">{am('detail.trackingSummary')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                {am('detail.mediaEnabled')}
              </span>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {profile !== undefined ? (
                  <CollectionChips media={profile.media} />
                ) : (
                  <Absent reason="not_observable" />
                )}
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                {am('detail.comments')}
              </span>
              <div className="flex items-center gap-1.5 pt-1 text-sm font-medium">
                {commentsEnabled ? (
                  <>
                    <CheckCircle2 className="size-4 text-primary-ink" aria-hidden="true" />
                    <span>{commentCap ? `${commentCap} cap/post` : am('detail.enabled')}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="size-4 text-muted-foreground" aria-hidden="true" />
                    <span>{am('detail.disabled')}</span>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                {am('detail.likers')}
              </span>
              <div className="flex items-center gap-1.5 pt-1 text-sm font-medium">
                {likersEnabled ? (
                  <>
                    <CheckCircle2 className="size-4 text-primary-ink" aria-hidden="true" />
                    <span>{am('detail.enabled')}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="size-4 text-muted-foreground" aria-hidden="true" />
                    <span>{am('detail.disabled')}</span>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                {am('detail.audioFanout')}
              </span>
              <div className="flex items-center gap-1.5 pt-1 text-sm font-medium">
                {audioFanout ? (
                  <>
                    <CheckCircle2 className="size-4 text-primary-ink" aria-hidden="true" />
                    <span>{am('detail.enabled')}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="size-4 text-muted-foreground" aria-hidden="true" />
                    <span>{am('detail.disabled')}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
